import React from 'react';

const RegmailCustomizationSection = ({ settings, onUpdate }) => {
  const handleChange = (key, value) => {
    onUpdate(key, value);
  };

  // Format date for display (YYYY-MM-DD format for input fields)
  const formatDateForInput = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // Format date for display (human readable format)
  const formatDateForDisplay = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Regmail Event Variables
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Customize the event-specific variables for your Regmail template.
          These variables appear in the email content using placeholders and
          will be substituted by the server.
        </p>
      </div>

      {/* Event Name */}
      <div>
        <label
          htmlFor="eventName"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Event Name
          <span
            className="ml-1 text-gray-400"
            title="Your event name (e.g., 'Your Event Name 2026')"
          >
            ⓘ
          </span>
        </label>
        <input
          type="text"
          id="eventName"
          value={settings.eventName || ''}
          onChange={e => handleChange('eventName', e.target.value)}
          placeholder="e.g., Your Event Name 2026"
          className="form-input block w-full"
        />
      </div>

      {/* Hosting Organisation */}
      <div>
        <label
          htmlFor="hostingOrganisation"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Hosting Organisation
          <span
            className="ml-1 text-gray-400"
            title="The organization hosting the event (e.g., 'Your Organisation')"
          >
            ⓘ
          </span>
        </label>
        <input
          type="text"
          id="hostingOrganisation"
          value={settings.hostingOrganisation || ''}
          onChange={e => handleChange('hostingOrganisation', e.target.value)}
          placeholder="e.g., Your Organisation"
          className="form-input block w-full"
        />
      </div>

      {/* Event Location */}
      <div>
        <label
          htmlFor="eventLocation"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Event Location
          <span
            className="ml-1 text-gray-400"
            title="Location of the event (e.g., 'City, Country')"
          >
            ⓘ
          </span>
        </label>
        <input
          type="text"
          id="eventLocation"
          value={settings.eventLocation || ''}
          onChange={e => handleChange('eventLocation', e.target.value)}
          placeholder="e.g., City, Country"
          className="form-input block w-full"
        />
      </div>

      {/* Event Start Date */}
      <div>
        <label
          htmlFor="eventStartDate"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Event Start Date
          <span
            className="ml-1 text-gray-400"
            title="The date when the event begins"
          >
            ⓘ
          </span>
        </label>
        <input
          type="date"
          id="eventStartDate"
          value={formatDateForInput(settings.eventStartDate) || '2026-01-01'}
          onChange={e => handleChange('eventStartDate', e.target.value)}
          className="form-input block w-full"
        />
      </div>

      {/* Event End Date */}
      <div>
        <label
          htmlFor="eventEndDate"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Event End Date
          <span
            className="ml-1 text-gray-400"
            title="The date when the event ends"
          >
            ⓘ
          </span>
        </label>
        <input
          type="date"
          id="eventEndDate"
          value={formatDateForInput(settings.eventEndDate) || '2026-01-31'}
          onChange={e => handleChange('eventEndDate', e.target.value)}
          className="form-input block w-full"
        />
      </div>

      {/* Event Time Display (read-only, shows formatted dates) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Date Range (Auto-generated)
          <span
            className="ml-1 text-gray-400"
            title="This field automatically displays the formatted date range based on your start and end date selections"
          >
            ⓘ
          </span>
        </label>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
          {settings.eventStartDate && settings.eventEndDate
            ? `${formatDateForDisplay(settings.eventStartDate)} - ${formatDateForDisplay(settings.eventEndDate)}`
            : 'Please select start and end dates above'}
        </div>
      </div>

      {/* Payment Due Date */}
      <div>
        <label
          htmlFor="paymentDueDate"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Payment Due Date
          <span
            className="ml-1 text-gray-400"
            title="Date by which payment is due (default: January 1, 2030)"
          >
            ⓘ
          </span>
        </label>
        <input
          type="date"
          id="paymentDueDate"
          value={formatDateForInput(settings.paymentDueDate) || '2026-01-01'}
          onChange={e => handleChange('paymentDueDate', e.target.value)}
          className="form-input block w-full"
        />
      </div>

      {/* Event Email */}
      <div>
        <label
          htmlFor="eventEmail"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Event Email Address
          <span
            className="ml-1 text-gray-400"
            title="The email address for event-related inquiries (e.g., info@yourevent.org)"
          >
            ⓘ
          </span>
        </label>
        <input
          type="email"
          id="eventEmail"
          value={settings.eventEmail || ''}
          onChange={e => handleChange('eventEmail', e.target.value)}
          placeholder="e.g., info@yourevent.org"
          className="form-input block w-full"
        />
      </div>
    </div>
  );
};

export default RegmailCustomizationSection;
